import React, { useState, useEffect } from 'react';
import { Shield, Plus, Pencil, Trash2, UserPlus, Power } from 'lucide-react';
import C from '../../constants/colors';
import { useAuth } from '../../context/AuthContext';
import * as usersApi from '../../api/users';
import * as permissionsApi from '../../api/permissions';
import RolePill from '../ui/RolePill';
import Toggle from '../ui/Toggle';
import UserEditorModal from '../modals/UserEditorModal';
import ProfileEditorModal from '../modals/ProfileEditorModal';

const CATEGORY_ORDER = ['Operations', 'Visitors', 'HSE', 'T&A', 'Admin'];

export default function AccessRightsView() {
  const { user: me, refreshPermissions } = useAuth();

  const [users, setUsers]                         = useState([]);
  const [linkableEmployees, setLinkableEmployees] = useState([]);
  const [allViews, setAllViews]                   = useState([]);
  const [profiles, setProfiles]                   = useState([]);
  const [selectedUserId, setSelectedUserId]       = useState(null);
  const [draftPerms, setDraftPerms]               = useState(new Set());
  const [selectedUserRole, setSelectedUserRole]   = useState(null);
  const [tab, setTab]                             = useState('permissions');
  const [profileModal, setProfileModal]           = useState(null);
  const [userModal, setUserModal]                 = useState(null); // null | 'new' | user object
  const [applyProfileId, setApplyProfileId]       = useState('');
  const [saving, setSaving]                       = useState(false);
  const [saveMsg, setSaveMsg]                     = useState('');
  const [loading, setLoading]                     = useState(true);
  const [actionMsg, setActionMsg]                 = useState('');

  useEffect(() => {
    Promise.all([
      usersApi.listUsers(),
      usersApi.getLinkableEmployees(),
      permissionsApi.listViews(),
      permissionsApi.listProfiles(),
    ]).then(([usersRes, empRes, viewsRes, profilesRes]) => {
      setUsers(Array.isArray(usersRes) ? usersRes : (usersRes.users || []));
      setLinkableEmployees(Array.isArray(empRes) ? empRes : []);
      setAllViews(viewsRes);
      setProfiles(profilesRes);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function selectUser(uid) {
    setSelectedUserId(uid);
    setSaveMsg('');
    setActionMsg('');
    try {
      const data = await permissionsApi.getUserPermissions(uid);
      setDraftPerms(new Set(data.viewKeys));
      setSelectedUserRole(data.role);
    } catch {
      setDraftPerms(new Set());
    }
  }

  async function handleApplyProfile() {
    if (!applyProfileId || !selectedUserId) return;
    try {
      const data = await permissionsApi.applyProfile(selectedUserId, applyProfileId);
      setDraftPerms(new Set(data.viewKeys));
      setSaveMsg('Profile applied — click Save to confirm.');
    } catch {
      setSaveMsg('Failed to apply profile.');
    }
  }

  async function handleSave() {
    if (!selectedUserId) return;
    setSaving(true);
    setSaveMsg('');
    try {
      await permissionsApi.setUserPermissions(selectedUserId, [...draftPerms]);
      setSaveMsg('Saved.');
      if (selectedUserId === me?.id) await refreshPermissions();
    } catch {
      setSaveMsg('Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateUser(data) {
    const created = await usersApi.createUser(data);
    setUsers(u => [...u, created].sort((a, b) => a.name.localeCompare(b.name)));
    setLinkableEmployees(list =>
      list.map(e => e.id === data.employeeRecordId ? { ...e, linkedUserId: created.id } : e)
    );
  }

  async function handleUpdateUser(id, data) {
    const updated = await usersApi.updateUser(id, data);
    setUsers(u => u.map(x => x.id === id ? { ...x, ...updated } : x));
    if (id === selectedUserId) setSelectedUserRole(updated.role);
    if (data.employeeRecordId !== undefined) {
      const prev = users.find(u => u.id === id)?.employeeRecordId;
      setLinkableEmployees(list =>
        list.map(e => {
          if (e.id === prev) return { ...e, linkedUserId: null };
          if (e.id === data.employeeRecordId) return { ...e, linkedUserId: id };
          return e;
        })
      );
    }
  }

  async function handleToggleActive(u) {
    const next = !u.isActive;
    try {
      const updated = await usersApi.updateUser(u.id, { isActive: next });
      setUsers(list => list.map(x => x.id === u.id ? { ...x, ...updated } : x));
      setActionMsg(next ? 'User activated.' : 'User deactivated.');
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Failed to update status.');
    }
  }

  async function handleDeleteUser(u) {
    if (!window.confirm(`Delete user "${u.name}"? This cannot be undone.`)) return;
    try {
      await usersApi.deleteUser(u.id);
      setUsers(list => list.filter(x => x.id !== u.id));
      if (u.employeeRecordId) {
        setLinkableEmployees(list =>
          list.map(e => e.id === u.employeeRecordId ? { ...e, linkedUserId: null } : e)
        );
      }
      if (selectedUserId === u.id) { setSelectedUserId(null); setDraftPerms(new Set()); }
      setActionMsg('User deleted.');
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Failed to delete user.');
    }
  }

  async function handleCreateProfile(data) {
    const created = await permissionsApi.createProfile(data);
    setProfiles(p => [...p, created]);
  }

  async function handleUpdateProfile(id, data) {
    const updated = await permissionsApi.updateProfile(id, data);
    setProfiles(p => p.map(x => x.id === id ? updated : x));
  }

  async function handleDeleteProfile(id) {
    if (!window.confirm('Delete this profile?')) return;
    try {
      await permissionsApi.deleteProfile(id);
      setProfiles(p => p.filter(x => x.id !== id));
    } catch (err) {
      alert(err.response?.data?.error || 'Cannot delete profile');
    }
  }

  const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
    acc[cat] = allViews.filter(v => v.category === cat);
    return acc;
  }, {});

  const selectedUser = users.find(u => u.id === selectedUserId);
  const isAdminUser  = ['ADMIN', 'SUPER_ADMIN'].includes(selectedUserRole);
  const isSelf       = selectedUserId === me?.id;

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted }}>
      Loading…
    </div>
  );

  return (
    <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>

      {/* ── Left panel: user list ── */}
      <div style={{ width: '260px', background: C.surface, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ padding: '12px 12px 8px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Shield size={15} color={C.blue} />
              <span style={{ fontSize: '13px', fontWeight: 700, color: C.textPrimary }}>Access Rights</span>
            </div>
            <button
              onClick={() => setUserModal('new')}
              title="Add user"
              style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 9px', borderRadius: '7px', border: `1px solid ${C.blue}44`, background: C.blue + '18', color: C.blue, fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              <UserPlus size={12} />Add
            </button>
          </div>
          <div style={{ fontSize: '11px', color: C.textMuted }}>Select a user to manage their access</div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '6px' }}>
          {users.length === 0 && (
            <div style={{ padding: '16px', fontSize: '12px', color: C.textMuted, textAlign: 'center' }}>No users found</div>
          )}
          {users.map(u => {
            const isActive   = u.isActive !== false;
            const isSelected = selectedUserId === u.id;
            return (
              <div
                key={u.id}
                onClick={() => selectUser(u.id)}
                style={{
                  padding: '9px 10px', borderRadius: '8px', cursor: 'pointer', marginBottom: '2px',
                  background: isSelected ? C.blue + '18' : 'transparent',
                  border: isSelected ? `1px solid ${C.blue}44` : '1px solid transparent',
                  opacity: isActive ? 1 : 0.55,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span
                    title={isActive ? 'Active' : 'Inactive'}
                    style={{ width: '7px', height: '7px', borderRadius: '50%', background: isActive ? C.green : C.border, flexShrink: 0 }}
                  />
                  <span style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                    {u.name}
                  </span>
                  <RolePill role={u.role} />
                </div>
                <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingLeft: '13px' }}>
                  {u.email}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Right panel ── */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, padding: '0 20px', background: C.surface }}>
          {[['permissions', 'User Permissions'], ['profiles', 'Permission Profiles']].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                padding: '14px 16px', fontSize: '13px', fontWeight: 600,
                color: tab === key ? C.blue : C.textMuted,
                borderBottom: tab === key ? `2px solid ${C.blue}` : '2px solid transparent',
                marginBottom: '-1px',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── Permissions tab ── */}
        {tab === 'permissions' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
            {!selectedUserId ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '200px', color: C.textMuted, fontSize: '13px', gap: '8px' }}>
                <Shield size={28} color={C.border} />
                Select a user from the left panel to manage their permissions
              </div>
            ) : (
              <>
                {/* User header with actions */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px', gap: '12px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '15px', fontWeight: 700, color: C.textPrimary }}>{selectedUser?.name}</span>
                      {selectedUserRole && <RolePill role={selectedUserRole} />}
                      {selectedUser?.isActive === false && (
                        <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '20px', background: C.border + '55', color: C.textMuted, border: `1px solid ${C.border}` }}>INACTIVE</span>
                      )}
                    </div>
                    <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>{selectedUser?.email}</div>
                    {actionMsg && <div style={{ fontSize: '12px', color: C.green, marginTop: '4px' }}>{actionMsg}</div>}
                  </div>

                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    <button
                      onClick={() => setUserModal(selectedUser)}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '7px', border: `1px solid ${C.border}`, background: 'none', color: C.textSecondary, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}
                    >
                      <Pencil size={11} />Edit
                    </button>
                    {!isSelf && (
                      <button
                        onClick={() => selectedUser && handleToggleActive(selectedUser)}
                        title={selectedUser?.isActive === false ? 'Activate user' : 'Deactivate user'}
                        style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '7px', border: `1px solid ${C.amber}44`, background: 'none', color: C.amber, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}
                      >
                        <Power size={11} />{selectedUser?.isActive === false ? 'Activate' : 'Deactivate'}
                      </button>
                    )}
                    {!isSelf && (
                      <button
                        onClick={() => selectedUser && handleDeleteUser(selectedUser)}
                        style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '7px', border: `1px solid ${C.red}44`, background: 'none', color: C.red, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}
                      >
                        <Trash2 size={11} />Delete
                      </button>
                    )}
                  </div>
                </div>

                {isAdminUser ? (
                  <div style={{ background: C.blue + '12', border: `1px solid ${C.blue}33`, borderRadius: '8px', padding: '12px 14px', fontSize: '13px', color: C.textSecondary, marginBottom: '16px' }}>
                    Admin and Super Admin users always have full access to all views — individual view grants do not apply.
                  </div>
                ) : (
                  <>
                    {/* Profile quick-apply */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', padding: '12px 14px', background: C.surfaceElevated, borderRadius: '8px', border: `1px solid ${C.border}` }}>
                      <span style={{ fontSize: '12px', color: C.textMuted, whiteSpace: 'nowrap' }}>Apply profile:</span>
                      <select
                        value={applyProfileId}
                        onChange={e => setApplyProfileId(e.target.value)}
                        style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '6px 8px', color: C.textPrimary, fontSize: '12px', fontFamily: 'inherit', cursor: 'pointer', outline: 'none' }}
                      >
                        <option value="">— select a profile —</option>
                        {profiles.map(p => (
                          <option key={p.id} value={p.id}>{p.name} ({p.viewKeys?.length || 0} views)</option>
                        ))}
                      </select>
                      <button
                        onClick={handleApplyProfile}
                        disabled={!applyProfileId}
                        style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', background: applyProfileId ? C.blue : C.border, color: applyProfileId ? '#fff' : C.textMuted, fontSize: '12px', fontWeight: 600, cursor: applyProfileId ? 'pointer' : 'default', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                      >
                        Apply
                      </button>
                    </div>

                    {/* View toggles by category */}
                    {CATEGORY_ORDER.map(cat => {
                      const views = grouped[cat];
                      if (!views?.length) return null;
                      return (
                        <div key={cat} style={{ marginBottom: '20px' }}>
                          <div style={{ fontSize: '10px', color: C.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '8px' }}>{cat}</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {views.map(v => (
                              <div
                                key={v.key}
                                style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: C.surfaceElevated, borderRadius: '8px', border: `1px solid ${draftPerms.has(v.key) ? C.blue + '44' : C.border}` }}
                              >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary }}>{v.label}</div>
                                  {v.description && <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '1px' }}>{v.description}</div>}
                                </div>
                                <Toggle
                                  on={draftPerms.has(v.key)}
                                  onChange={on => {
                                    const next = new Set(draftPerms);
                                    on ? next.add(v.key) : next.delete(v.key);
                                    setDraftPerms(next);
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}

                    {/* Save bar */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        style={{ padding: '9px 24px', borderRadius: '8px', border: 'none', background: C.blue, color: '#fff', fontSize: '13px', fontWeight: 600, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1, fontFamily: 'inherit' }}
                      >
                        {saving ? 'Saving…' : 'Save Permissions'}
                      </button>
                      {saveMsg && <span style={{ fontSize: '12px', color: saveMsg.includes('fail') || saveMsg.includes('Failed') ? C.red : C.green }}>{saveMsg}</span>}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Profiles tab ── */}
        {tab === 'profiles' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', color: C.textMuted }}>Named view bundles — apply to users as a starting point</div>
              <button
                onClick={() => setProfileModal('new')}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', border: `1px solid ${C.blue}44`, background: C.blue + '18', color: C.blue, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                <Plus size={13} />New Profile
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {profiles.map(p => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '8px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: C.textPrimary }}>{p.name}</span>
                      {p.isSystem && (
                        <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '20px', background: C.blue + '22', color: C.blue, border: `1px solid ${C.blue}44` }}>SYSTEM</span>
                      )}
                      <span style={{ fontSize: '11px', color: C.textMuted }}>{p.viewKeys?.length || 0} views</span>
                    </div>
                    {p.description && <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>{p.description}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={() => setProfileModal(p)}
                      style={{ padding: '5px 10px', borderRadius: '6px', border: `1px solid ${C.border}`, background: 'none', color: C.textSecondary, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      <Pencil size={11} />Edit
                    </button>
                    <button
                      onClick={() => !p.isSystem && handleDeleteProfile(p.id)}
                      disabled={p.isSystem}
                      title={p.isSystem ? 'System profiles cannot be deleted' : 'Delete profile'}
                      style={{ padding: '5px 10px', borderRadius: '6px', border: `1px solid ${p.isSystem ? C.border : C.red + '44'}`, background: 'none', color: p.isSystem ? C.textMuted : C.red, fontSize: '12px', cursor: p.isSystem ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '4px', opacity: p.isSystem ? 0.5 : 1 }}
                    >
                      <Trash2 size={11} />Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── User editor modal ── */}
      {userModal && (
        <UserEditorModal
          user={userModal === 'new' ? null : userModal}
          linkableEmployees={linkableEmployees}
          onSave={async (data) => {
            if (userModal === 'new') {
              await handleCreateUser(data);
            } else {
              await handleUpdateUser(userModal.id, data);
            }
          }}
          onClose={() => setUserModal(null)}
        />
      )}

      {/* ── Profile editor modal ── */}
      {profileModal && (
        <ProfileEditorModal
          profile={profileModal === 'new' ? null : profileModal}
          allViews={allViews}
          onSave={async (data) => {
            if (profileModal === 'new') {
              await handleCreateProfile(data);
            } else {
              await handleUpdateProfile(profileModal.id, data);
            }
          }}
          onClose={() => setProfileModal(null)}
        />
      )}
    </div>
  );
}
